import { useState } from "react";
import { X, Loader2, Download, Check } from "lucide-react";
import { GroupChecklist } from "@/components/GroupMultiFilter";

export const REPORT_SECTIONS = [
  { key: "final", label: "Nilai Akhir & Status", desc: "nilai akhir terbobot dan status kelulusan" },
  { key: "chart", label: "Grafik", desc: "grafik progres nilai (PDF) / grafik nilai akhir (Excel)" },
  { key: "kehadiran", label: "Kehadiran", desc: "jumlah hadir dan nilainya" },
  { key: "partisipasi", label: "Partisipasi Aktif", desc: "rincian tugas partisipasi" },
  { key: "kuis", label: "Kuis", desc: "nilai tiap kuis" },
  { key: "individu", label: "Presentasi Individu", desc: "rincian tugas individu" },
  { key: "kelompok", label: "Presentasi Kelompok", desc: "rincian tugas kelompok" },
];

const DEFAULT = ["final", "kehadiran", "partisipasi", "kuis", "individu", "kelompok"];

export const ReportOptionsDialog = ({ title, subtitle, onClose, onDownload, groupNames, initialGroups = [] }) => {
  const [sel, setSel] = useState(DEFAULT);
  const [groups, setGroups] = useState(initialGroups);
  const [busy, setBusy] = useState(false);
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const all = sel.length === REPORT_SECTIONS.length;

  const submit = async () => {
    setBusy(true);
    try { await onDownload(REPORT_SECTIONS.map((s) => s.key).filter((k) => sel.includes(k)).join(","), groups); onClose(); }
    catch { setBusy(false); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="report-options-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <button data-testid="report-options-close" onClick={onClose} className="absolute right-4 top-4"><X className="h-5 w-5 text-slate-400" /></button>
        <h3 className="font-heading text-xl font-bold text-slate-900">{title}</h3>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        {groupNames && (
          <div className="mt-4" data-testid="report-groups">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Kelompok karyawan</p>
            <div className="rounded-xl border border-slate-200 p-1.5">
              <GroupChecklist names={groupNames} value={groups} onChange={setGroups} testid="report-group" />
            </div>
          </div>
        )}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Isi laporan</p>
          <button data-testid="report-options-toggle-all" onClick={() => setSel(all ? [] : REPORT_SECTIONS.map((s) => s.key))} className="text-xs font-semibold text-blue-600 hover:underline">
            {all ? "Kosongkan" : "Pilih semua"}
          </button>
        </div>
        <div className="mt-2 space-y-1.5">
          {REPORT_SECTIONS.map((s) => {
            const on = sel.includes(s.key);
            return (
              <button key={s.key} data-testid={`report-section-${s.key}`} onClick={() => toggle(s.key)}
                className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${on ? "border-blue-300 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${on ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white"}`}>
                  {on && <Check className="h-3.5 w-3.5" />}
                </span>
                <span>
                  <span className="block text-sm font-semibold text-slate-800">{s.label}</span>
                  <span className="block text-xs text-slate-500">{s.desc}</span>
                </span>
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-slate-400">Komponen yang tidak dinilai pada kelompok karyawan tetap tidak ditampilkan.</p>
        <button data-testid="report-options-download" onClick={submit} disabled={busy || sel.length === 0}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {sel.length === 0 ? "Pilih minimal satu bagian" : "Unduh Laporan"}
        </button>
      </div>
    </div>
  );
};
